<template>
  <div class="dashboard-card">
    <div class="card-icon">
      <i :class="`fas ${metric.icon}`"></i>
    </div>
    <div class="card-content">
      <div class="card-header-info">
        <p class="card-label">{{ metric.label }}</p>
        <span class="card-badge" v-if="metric.description">{{ metric.description }}</span>
      </div>
      <h3 class="card-value">{{ formattedValue }}</h3>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DashboardCard',

  props: {
    /**
     * Metric object containing card data
     * @type {Object}
     * @property {string} key - Unique identifier
     * @property {string} label - Display label
     * @property {number} value - Numeric value to display
     * @property {string} icon - FontAwesome icon class (e.g., 'fa-building')
     * @property {string} color - Color variant (primary, success, info, warning)
     */
    metric: {
      type: Object,
      required: true,
      validator(value) {
        return (
          value.key &&
          value.label &&
          value.value !== undefined &&
          value.icon &&
          value.color
        )
      }
    }
  },

  computed: {
    /**
     * Format large numbers with thousand separators
     * Examples: 1000 → 1,000 | 1000000 → 1,000,000
     * @returns {string} Formatted number string
     */
    formattedValue() {
      const value = this.metric.value

      if (typeof value !== 'number') {
        return value
      }

      // Format with thousand separators
      return value.toLocaleString('en-US')
    }
  }
}
</script>

<style scoped>
/* Dashboard Card Container */
.dashboard-card {
  background: white;
  border-radius: var(--extranet-rounded);
  border: 1px solid #e2e8f0;
  padding: 32px;
  display: flex;
  align-items: center;
  gap: 24px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.dashboard-card::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: var(--extranet-primary);
  opacity: 0;
  transition: opacity 0.3s;
}

.dashboard-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px -10px rgba(0, 0, 0, 0.1);
  border-color: #cbd5e1;
}

.dashboard-card:hover::after {
  opacity: 1;
}

/* Card Icon */
.card-icon {
  width: 64px;
  height: 64px;
  background: #fff5f5;
  border-radius: var(--extranet-rounded);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.3s;
}

.dashboard-card:hover .card-icon {
  background: var(--extranet-primary);
  transform: rotate(-3deg);
}

.card-icon i {
  font-size: 24px;
  color: var(--extranet-primary);
  transition: color 0.3s;
}

.dashboard-card:hover .card-icon i {
  color: white;
}

/* Card Content */
.card-content {
  flex: 1;
  min-width: 0;
}

.card-header-info {
  display: flex;
  flex-direction: column;
  margin-bottom: 8px;
}

.card-label {
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.card-badge {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 2px;
}

.card-value {
  font-size: 32px;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
  line-height: 1;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .dashboard-card {
    padding: 16px;
    min-height: 100px;
  }

  .card-icon {
    width: 48px;
    height: 48px;
  }

  .card-icon i {
    font-size: 20px;
  }

  .card-value {
    font-size: 24px;
  }

  .card-label {
    font-size: 13px;
  }
}

@media (max-width: 768px) {
  .dashboard-card {
    padding: 16px;
    gap: 12px;
    min-height: 90px;
  }

  .card-icon {
    width: 44px;
    height: 44px;
  }

  .card-icon i {
    font-size: 18px;
  }

  .card-value {
    font-size: 22px;
  }

  .card-label {
    font-size: 12px;
  }
}

@media (max-width: 576px) {
  .dashboard-card {
    flex-direction: column;
    text-align: center;
    padding: 20px 16px;
  }

  .card-icon {
    width: 52px;
    height: 52px;
  }

  .card-icon i {
    font-size: 22px;
  }

  .card-value {
    font-size: 26px;
  }

  .card-label {
    font-size: 13px;
  }
}
</style>
